import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { ListLoader } from '../../../../../shared/components/list-loader/list-loader';
import {
  AuftragExportTypeDetailEntryMeta,
  BatchAuftragService,
  MultilanguageTextMeta,
  TemplateDetailEntryMeta
} from '../../../../../core/batch-auftrag-api';
import { ListLoaderObservable } from '../../../../../shared/components/list-loader/list-loader-observable';
import { map } from 'rxjs/operators';
import { keysToTranslate } from 'i18n/keys-to-translate';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { FormsModule } from '@angular/forms';
import { CapFormColumnsDirective } from '../../../../../shared/form-control/form-columns.directive';
import { MetaDisablerDirective } from '../../../../../shared/directives/meta-disabler.directive';
import { MetaValidatorDirective } from '../../../../../shared/directives/meta-validator.directive';
import { ListLoaderWrapperComponent } from '../../../../../shared/components/list-loader-wrapper/list-loader-wrapper.component';
import { NgTemplateOutlet } from '@angular/common';
import { RouterLink } from '@angular/router';
import { CdkStepperNext } from '@angular/cdk/stepper';
import { TranslateNamePipe } from '../../../../../shared/pipes/simple/translate-name.pipe';

@Component({
  selector: 'cap-select-auftragstyp',
  templateUrl: './select-auftragstyp.component.html',
  styleUrls: ['./select-auftragstyp.component.scss'],
  imports: [
    FormsModule,
    CapFormColumnsDirective,
    MetaDisablerDirective,
    MetaValidatorDirective,
    ListLoaderWrapperComponent,
    NgTemplateOutlet,
    RouterLink,
    CdkStepperNext,
    TranslatePipe,
    TranslateNamePipe
  ]
})
export class SelectAuftragstypComponent implements OnInit, OnDestroy {
  itemsList: AuftragExportType[];
  radioTypeSelectedIndex: string;
  currentItemSelected: AuftragExportType;

  templateListLoader: ListLoader<AuftragExportTemplate>;
  templateSelectedBezeichnung: string;
  currentTemplateSelected: AuftragExportTemplate;

  @Input()
  readonly: boolean;

  get template() {
    return this.currentTemplateSelected;
  }

  @Output()
  templateChange: EventEmitter<AuftragExportTemplate> = new EventEmitter<AuftragExportTemplate>();

  @Input()
  set template(val: any) {
    this.currentTemplateSelected = val;
    if (!this.readonly) {
      this.templateChange.emit(this.currentTemplateSelected);
    }
  }

  constructor(
    private batchAuftragService: BatchAuftragService,
    private translate: TranslateService
  ) {
    keysToTranslate<string>([
      'datenexport.GELOESCHTBODENBEDGS',
      'datenexport.GELAN',
      'datenexport.RECHTEREGISTER',
      'datenexport.BODENBEDECKUNGSFILTER',
      'datenexport.EIGENTUEMERART',
      'datenexport.EIGENTUEMERLISTE',
      'datenexport.EIGENTUEMERREGISTER',
      'datenexport.GEAENDERTEGS',
      'datenexport.GEAENDERTEBODENBEDGS',
      'datenexport.GEBAEUDELISTE',
      'datenexport.GELOESCHTEGS',
      'datenexport.GRUNDSTUECKLISTE',
      'datenexport.STANDARD'
    ]);
  }

  ngOnInit() {
    this.batchAuftragService.searchAuftragTypes().subscribe((types) => {
      this.itemsList = this.toTypeVM(types.auftragTypeList);
      this.radioTypeSelectedIndex = '0';
      this.updateTypeSelection();
    });
  }

  updateTypeSelection() {
    if (this.readonly) {
      this.currentItemSelected = this.itemsList.find(
        (value) => value.model.exportTyp === this.currentTemplateSelected.model.exportTypDefiniton.exportTyp
      );
      this.radioTypeSelectedIndex = this.currentItemSelected.value;
      return;
    }
    if (this.itemsList) {
      this.currentItemSelected = this.itemsList[+this.radioTypeSelectedIndex];
      this.template = null;
      this.templateSelectedBezeichnung = null;
      if (this.currentItemSelected && this.currentItemSelected.model) {
        const observable = this.batchAuftragService
          .searchAuftragExportTypeTemplates(this.currentItemSelected.model)
          .pipe(map((a) => this.toTemplateVM(a.auftragTypeList)));
        this.templateListLoader = new ListLoaderObservable<AuftragExportTemplate>(undefined, 1, observable);
      }
    }
  }

  updateTemplateSelection() {
    if (this.readonly) {
      return;
    }
    this.template = null;
    if (this.templateListLoader && this.templateListLoader.items && this.templateSelectedBezeichnung) {
      this.template = this.templateListLoader.items.find((a) => a.bezeichnung === this.templateSelectedBezeichnung);
    }
  }

  toTypeVM(types: Array<AuftragExportTypeDetailEntryMeta>) {
    const typeList = new Array<AuftragExportType>();
    types
      .sort((a, b) => this.sortAuftragsTypen(a.exportTyp, b.exportTyp))
      .map((t) => new AuftragExportType(t, '' + types.indexOf(t)))
      .forEach((x) => typeList.push(x));
    return typeList;
  }

  private sortAuftragsTypen(a, b): number {
    if (!a) {
      return -1;
    }
    if (!b) {
      return 1;
    }
    return this.translate.instant('datenexport.' + a).localeCompare(this.translate.instant('datenexport.' + b));
  }

  toTemplateVM(template: Array<TemplateDetailEntryMeta>) {
    const templates = new Array<AuftragExportTemplate>();
    template.map((t) => new AuftragExportTemplate(t)).forEach((x) => templates.push(x));
    return templates;
  }

  ngOnDestroy(): void {
    if (this.templateListLoader) {
      this.templateListLoader.unsubscribeAll();
    }
  }
}

class AuftragExportType {
  constructor(model: AuftragExportTypeDetailEntryMeta, index: string) {
    this.model = model;
    this.name = model.exportTyp;
    this.beschreibung = model.beschreibung.text;
    this.value = index;
  }

  model: AuftragExportTypeDetailEntryMeta;
  name: string;
  beschreibung: MultilanguageTextMeta;
  value: string;
}

export class AuftragExportTemplate {
  constructor(model: TemplateDetailEntryMeta) {
    this.model = model;
    this.bezeichnung = model.benutzerexporttypname;
    this.beschreibung = model.benutzerBeschreibung.text;
  }

  model: TemplateDetailEntryMeta;
  bezeichnung: string;
  beschreibung: MultilanguageTextMeta;
  selected: boolean;
}
